import type { AccessRequirement } from '@/lib/access-requirements';
import { useAuth } from '@/hooks/use-auth';
import { useCompany } from '@/hooks/use-company';
import { usePermissions } from '@/hooks/use-permissions';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

interface RequirePermissionProps {
  allOf?: readonly string[];
  anyOf?: readonly string[];
  access?: AccessRequirement;
  allAccessOf?: readonly AccessRequirement[];
  anyAccessOf?: readonly AccessRequirement[];
  children?: ReactNode;
}

const EMPTY_PERMISSION_CODES: readonly string[] = [];
const EMPTY_ACCESS_REQUIREMENTS: readonly AccessRequirement[] = [];

export function RequirePermission({
  allOf = EMPTY_PERMISSION_CODES,
  anyOf = EMPTY_PERMISSION_CODES,
  access,
  allAccessOf = EMPTY_ACCESS_REQUIREMENTS,
  anyAccessOf = EMPTY_ACCESS_REQUIREMENTS,
  children,
}: RequirePermissionProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { isLoading: isCompanyLoading } = useCompany();
  const { canAccess, canAccessAll, canAccessAny, canAll, canAny } = usePermissions();

  if (isAuthLoading || (isAuthenticated && isCompanyLoading)) {
    return (
      <div className="bg-background text-muted-foreground flex min-h-screen items-center justify-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Verificando permissões...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const hasRequiredAll = allOf.length === 0 || canAll(allOf);
  const hasRequiredAny = anyOf.length === 0 || canAny(anyOf);
  const hasRequiredAccess = !access || canAccess(access);
  const hasRequiredAccessAll = allAccessOf.length === 0 || canAccessAll(allAccessOf);
  const hasRequiredAccessAny = anyAccessOf.length === 0 || canAccessAny(anyAccessOf);

  if (!hasRequiredAll || !hasRequiredAny || !hasRequiredAccess || !hasRequiredAccessAll || !hasRequiredAccessAny) {
    return <Navigate to="/forbidden" replace state={{ from: location.pathname }} />;
  }

  if (children) {
    return <>{children}</>;
  }

  return <Outlet />;
}
